import {
  FormSchemaValidator,
  FormSchemaConverter,
  isInputElement,
  IFieldElementUnion,
  IFormSchemaV2,
  isStaticElement,
} from "@graffer-inc/form-schema";
import { ReservedApplicantIdHelper } from "../applicant-schema/applicant-reserved-ids";
import { CloudGovFormSchemaAccessor } from "./accessor-helper";
import { SmartApplyConditionalRuleValidator } from "./smart-apply-conditional-rule-validator";
import { deepEqual } from "../../../../utils/deep-equal";
import {
  getDefaultApplicantSectionComponentConfigToCompare,
  isReplacedApplicantCorporateSearchComponent,
  isReplacedApplicantPostalCodeComponent,
} from "../applicant-schema/index";
import type { ApplicantSchemaKeys } from "../applicant-schema-keys";
import { getTotalMaxFileSize } from "../form-schema-helper";

const ValidationConstant = {
  TOTAL_MIN_SECTION_COUNT: 2,
  TOTAL_MAX_SECTION_COUNT: 20,
  TOTAL_MAX_FILE_SIZE: 200,
  TOTAL_MAX_INPUT_ELEMENTS: 400,

  // FunctionElement が導入された結果として、既存データを
  // 不正なものと見做さないよう、総要素数の上限を拡張。
  // 400 => 405 という数字に根拠はないので、運用しつつ決めることが望ましい。
  TOTAL_MAX_FIELD_ELEMENTS: 405,
} as const;
export const PROCEDURE_MAX_FILE_SIZE = ValidationConstant.TOTAL_MAX_FILE_SIZE;

export type ValidateOpt = {
  withJPKI?: boolean;
  withMOJ?: boolean;
};

type FlattenElementsCache = ReturnType<
  typeof FormSchemaConverter.prototype.expand
>;

export class SmartApplyFormSchemaValidator extends FormSchemaValidator {
  private flattenElementsCache?: FlattenElementsCache;
  private applicantSchemaKeys: ApplicantSchemaKeys;

  constructor(schema: IFormSchemaV2, applicantSchemaKeys: ApplicantSchemaKeys) {
    super(schema);
    this.applicantSchemaKeys = applicantSchemaKeys;
  }

  validate(option?: ValidateOpt): string[] {
    const opt: ValidateOpt = {
      withJPKI: false,
      withMOJ: false,
      ...option,
    };
    const result = super.validate();
    this.append(result, this.totalSection());
    this.append(result, this.totalInputCount());
    this.append(result, this.totalFieldElementCount());
    this.append(result, this.totalAttachFileSize());
    this.append(result, this.applicantSectionExists());
    this.append(result, this.applicantTypeOptions());
    this.append(result, this.applicantSectionChildren());
    this.append(result, this.reservedElementIDs());
    // 以下の定義に従ってdateAndDateTimeFormatはassertionでは発火させずvalidateのみで発火させる
    // https://github.com/graffer-inc/graffer-schemas/blob/master/libs/packages/form-schema/lib/base-schema-validator.ts#L29-L35
    this.append(result, this.dateAndDateTimeFormat());

    if (result.length === 0) {
      // FormSchemaの本体側のチェックが通ったときのみ、条件付きルールのチェックにすすむ（条件付きルールがFormSchema本体が正しいことを前提にしているため）
      this.append(
        result,
        new SmartApplyConditionalRuleValidator(
          this.schema,
          this.applicantSchemaKeys,
        ).validate(),
      );
      this.append(result, this.signatureRequiredElements(opt));
    }
    return result;
  }

  validateDeep(option?: ValidateOpt): string[] {
    const opt: ValidateOpt = {
      withJPKI: false,
      withMOJ: false,
      ...option,
    };
    const result = super.validateDeep();
    if (result.length === 0) {
      this.append(result, this.signatureRequiredElements(opt));
    }
    return result;
  }

  private signatureRequiredElements(option: ValidateOpt): string[] {
    const result: string[] = [];

    const conditionalRuleValidator = new SmartApplyConditionalRuleValidator(
      this.schema,
      this.applicantSchemaKeys,
    );

    if (option.withJPKI) {
      this.append(
        result,
        conditionalRuleValidator.myNumberSignatureRequiredElements(),
      );
    }

    if (option.withMOJ) {
      this.append(
        result,
        conditionalRuleValidator.mojSignatureRequiredElements(),
      );
    }

    return result;
  }

  assertion(): void {
    super.assertion();
    this.assertError([
      this.totalSection(),
      this.totalInputCount(),
      this.totalAttachFileSize(),
      this.applicantSectionExists(),
      this.applicantTypeOptions(),
      this.applicantSectionChildren(),
      this.reservedElementIDs(),
    ]);
    new SmartApplyConditionalRuleValidator(
      this.schema,
      this.applicantSchemaKeys,
    ).assertion();
  }

  checkPublishable(opt: ValidateOpt) {
    return this.publishable(opt).length === 0;
  }

  publishable(opt: ValidateOpt): string[] {
    const errors = this.validate(opt);

    if (this.countSection() < ValidationConstant.TOTAL_MIN_SECTION_COUNT) {
      this.append(
        errors,
        `申請フォームは${ValidationConstant.TOTAL_MIN_SECTION_COUNT}ページ以上設定してください。`,
      );
    }

    if (this.emptySectionIndex() > -1) {
      this.append(
        errors,
        `申請フォーム${
          this.emptySectionIndex() + 1
        }ページに入力要素が設定されていません。`,
      );
    }

    return errors;
  }

  totalSection() {
    return this.countSection() > ValidationConstant.TOTAL_MAX_SECTION_COUNT
      ? `作成できるセクションは合計${ValidationConstant.TOTAL_MAX_SECTION_COUNT}までです`
      : undefined;
  }

  totalInputCount() {
    return this.getFlattenElements().filter(isInputElement).length >
      ValidationConstant.TOTAL_MAX_INPUT_ELEMENTS
      ? `入力要素の合計数は${ValidationConstant.TOTAL_MAX_INPUT_ELEMENTS}までです`
      : undefined;
  }

  totalFieldElementCount() {
    return this.getFlattenElements().length >
      ValidationConstant.TOTAL_MAX_FIELD_ELEMENTS
      ? `設定できる要素の合計数は${ValidationConstant.TOTAL_MAX_FIELD_ELEMENTS}までです`
      : undefined;
  }

  /**
   * トータルの添付ファイルの上限サイズの合計が200MB以下であること
   */
  totalAttachFileSize() {
    const totalFileSize = getTotalMaxFileSize(this.schema);
    return totalFileSize > ValidationConstant.TOTAL_MAX_FILE_SIZE
      ? `ファイル項目の最大サイズの合計値は${ValidationConstant.TOTAL_MAX_FILE_SIZE}MB以下に設定してください。`
      : undefined;
  }

  /**
   * 申請者セクションを有している必要があるという制約
   */
  applicantSectionExists() {
    return !ReservedApplicantIdHelper.isApplicantSectionId(
      this.schema.root[0].id,
      this.applicantSchemaKeys,
    )
      ? "申請者セクションが存在しません"
      : undefined;
  }

  /**
   * 申請者の種別のEnumが所定の値であるかどうかをチェックする
   */
  applicantTypeOptions() {
    try {
      this.accessor.supportedApplicantTypes;
      return undefined;
    } catch (e) {
      return "申請者の種別に設定されている値が不正です";
    }
  }

  /**
   * 申請者セクションの要素の以下の制約条件をチェックする
   *
   * 1. 事前定義されている要素以外の要素が含まれていないこと
   * 2. 事前定義されている要素と同じ設定項目になっていること
   * 3. 事前定義されている要素と同じ順序になっていること
   */
  applicantSectionChildren() {
    const applicantSection = this.accessor.applicantSection;
    if (!applicantSection) {
      return "申請者セクションの子要素が存在しません";
    }
    let maxIndex = 0;
    for (let idx = 0; idx < applicantSection.children.length; idx++) {
      const target = applicantSection.children[idx];
      const error = this.preserveDefaultConfig(target);
      if (error) {
        return error;
      }

      const { defaultIndex } =
        getDefaultApplicantSectionComponentConfigToCompare(
          target,
          this.applicantSchemaKeys,
        );

      // デフォルトの定義に法人検索コンポーネントがない状況での、臨時対応
      if (
        defaultIndex === -1 &&
        isReplacedApplicantCorporateSearchComponent(
          target,
          this.applicantSchemaKeys,
        )
      ) {
        continue;
      }
      if (maxIndex > defaultIndex) {
        return "申請者セクションの要素が予期しない順番で並んでいます";
      }
      maxIndex = defaultIndex;
    }
    return;
  }

  /**
   * 予め予約されているIDの用途が問題ないかチェックする
   */
  reservedElementIDs() {
    const schemaWithoutApplicantSection = {
      ...this.schema,
      // 申請者セクションに余計な値が入っていないかは、`applicantSectionChildren`で担保しているため、ここでは検証しない
      root: this.schema.root.filter(
        (v) =>
          !ReservedApplicantIdHelper.isApplicantSectionId(
            v.id,
            this.applicantSchemaKeys,
          ),
      ),
    };
    const allElementIDs = new FormSchemaConverter(
      schemaWithoutApplicantSection,
    ).allIds();
    for (const id of allElementIDs) {
      if (id.startsWith("applicant")) {
        return "予約されている申請者Prefixが利用されています";
      }
      if (id.startsWith("__")) {
        // 申請者モデルと同様な予約された特殊なPropertyを用意したくなったときのために、
        // `__`から始まるprefixのnamespaceは予約しておく
        return "予約されているPrefixが利用されています";
      }
    }
    return;
  }

  /**
   * form-generatorを使うと値が`YYYY/MM/DD`形式で入力されるため、この形式の設定のみに制限する
   * https://github.com/graffer-inc/graffer-design-kit/blob/a17b20fb61aeb7788ead6a92ff001d04f0117b70/src/components/molecules/day-input/use-day-input-controller.tsx#L52-L70
   *
   * 日付要素に設定されているdateFormatが`YYYY/MM/DD`であり、
   * 日付時間要素に設定されているdateTimeFormatが`YYYY/MM/DD HH:mm`であることをチェックする
   */
  dateAndDateTimeFormat(): string | undefined {
    for (const elem of this.flattener.flatten().filter(isInputElement)) {
      if (elem.inputType === "date") {
        if (elem.dateFormat !== "YYYY/MM/DD") {
          return `「${elem.inputType}」要素「${elem.label.text}」に設定されているdateFormatが「${elem.dateFormat}」になっています。「YYYY/MM/DD」に修正してください。`;
        }
      }
      if (elem.inputType === "datetime") {
        if (elem.dateTimeFormat !== "YYYY/MM/DD HH:mm") {
          return `「${elem.inputType}」要素「${elem.label.text}」に設定されているdateTimeFormatが「${elem.dateTimeFormat}」になっています。「YYYY/MM/DD HH:mm」に修正してください。`;
        }
      }
    }
    return;
  }

  // 項目未設定のセクションindex取得
  private emptySectionIndex() {
    return this.schema.root.findIndex(
      (s) => s.children.filter((e) => !isStaticElement(e)).length === 0,
    );
  }

  // セクション数取得
  private countSection() {
    return this.schema.root.length;
  }

  private get accessor() {
    return new CloudGovFormSchemaAccessor(
      this.schema,
      this.applicantSchemaKeys,
    );
  }

  private get flattener() {
    return new FormSchemaConverter(this.schema);
  }

  protected preserveDefaultConfig(elem: IFieldElementUnion) {
    const { element: preDefined } =
      getDefaultApplicantSectionComponentConfigToCompare(
        elem,
        this.applicantSchemaKeys,
      );
    if (!preDefined) {
      return "予期しない要素が申請者セクションに含まれています";
    }

    if (
      ReservedApplicantIdHelper.isApplicantType(
        elem.id,
        this.applicantSchemaKeys,
      )
    ) {
      // Enumの値が異なることがあるので比較から外す
      // ApplicantTypeの制約はsupportedApplicantTypeチェックで担保
      return;
    }

    const copied = JSON.parse(JSON.stringify(elem));
    delete copied["pdfAdapterRule"];
    delete copied["description"];
    delete copied["placeholder"];

    // descriptionの更新を許容するため、比較から外す
    // deleteのため型をanyまで落とすが、{...preDefined}だとdateFormatがdateFormatValueにコピーされてしまうため、json parseを利用する
    const castedPreDefined = JSON.parse(JSON.stringify(preDefined));
    delete castedPreDefined["description"];
    delete castedPreDefined["placeholder"];

    // 設定自由化を念頭に、 id も比較から外しておく
    delete copied["id"];
    delete castedPreDefined["id"];

    const comparer = isReplacedApplicantCorporateSearchComponent(
      elem,
      this.applicantSchemaKeys,
    )
      ? this.corporateSearchElementComparer.bind(this)
      : isReplacedApplicantPostalCodeComponent(elem, this.applicantSchemaKeys)
        ? this.postalCodeElementComparer.bind(this)
        : deepEqual;

    if (!comparer(copied, castedPreDefined)) {
      return "設定されている要素がデフォルトの設定値と異なっています";
    }
    return;
  }

  // 下記のようなケースで法人検索コンポーネントが挿入された場合を想定したケア。
  // https://graffer.slack.com/archives/CM36FHK6C/p1664351005505429?thread_ts=1664158946.534169&cid=CM36FHK6C
  private corporateSearchElementComparer(
    actual: any,
    preDefined: any,
  ): boolean {
    const { autocompleteTarget } = actual;

    if (
      !!autocompleteTarget.corporateName &&
      autocompleteTarget.corporateName !==
        this.applicantSchemaKeys.applicantName.id
    ) {
      return false;
    }
    delete actual.autocompleteTarget.corporateName;
    delete preDefined.autocompleteTarget.corporateName;

    if (
      !!autocompleteTarget.corporateNumber &&
      autocompleteTarget.corporateNumber !==
        this.applicantSchemaKeys.applicantCorporateNumber.id
    ) {
      return false;
    }
    delete actual.autocompleteTarget.corporateNumber;
    delete preDefined.autocompleteTarget.corporateNumber;

    if (
      !!autocompleteTarget.corporateAddress &&
      autocompleteTarget.corporateAddress !==
        this.applicantSchemaKeys.applicantAddress.id
    ) {
      return false;
    }
    delete actual.autocompleteTarget.corporateAddress;
    delete preDefined.autocompleteTarget.corporateAddress;

    if (
      !!autocompleteTarget.corporatePostalCode &&
      autocompleteTarget.corporatePostalCode !==
        this.applicantSchemaKeys.applicantPostalCode.id
    ) {
      return false;
    }
    delete actual.autocompleteTarget.corporatePostalCode;
    delete preDefined.autocompleteTarget.corporatePostalCode;

    return deepEqual(actual, preDefined);
  }

  private postalCodeElementComparer(actual: any, preDefined: any): boolean {
    if (
      actual.addressCompleteTarget !==
      this.applicantSchemaKeys.applicantAddress.id
    ) {
      return false;
    }
    delete actual.addressCompleteTarget;
    delete preDefined.addressCompleteTarget;

    return deepEqual(actual, preDefined);
  }

  private getFlattenElements(): FlattenElementsCache {
    if (this.flattenElementsCache) {
      return this.flattenElementsCache;
    }

    const flattenElements = this.flattener.expand();
    this.flattenElementsCache = flattenElements;
    return flattenElements;
  }
}
